// src/components/admin/scheduling/student_calendar/ModalCalendarPanel.js
//
// The Student Calendar embedded inside the Create/Edit Session modal.
// Uses the EXACT same DayCell and MonthSection components as the full
// Student Calendar page (StudentCalendar.js) via the shared StudentCalendarGrid.js.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import {
  addMonths,
  monthGridDays,
  startOfMonth,
} from "../../employee/availability/availabilityUtils";
import {
  MonthSection,
  monthKey,
  ymd,
  toDate,
  fmtTime,
  isPastDateValue,
} from "./StudentCalendarGrid";
import {
  buildTimeOptions,
  formatTimeFromMinutes,
  minutesFromDate,
} from "../timeUtils";
import { resolveScheduleForDate } from "../scheduleSettingsUtils";
import {
  fetchDayConfigsForDates,
  subscribeToStudentSessionsInRange,
  fetchAvailabilityBlocks,
  fetchScheduledSessions,
  updateScheduledSession,
  createScheduledSession,
} from "../schedulingApi";

const PAST_INIT = 2;
const FUTURE_INIT = 4;
const LOAD_PAST = 3;
const LOAD_FUTURE = 3;
const SUB_PAST_MONTHS = 18;
const SUB_FUTURE_MONTHS = 18;

function overlaps(as, ae, bs, be) {
  return as < be && bs < ae;
}

function isCanceledSt(status) {
  return String(status || "").toLowerCase().includes("cancel");
}

function toMin(val) {
  const date = val instanceof Date ? val : (val?.toDate?.() ?? new Date(val));
  return date.getHours() * 60 + date.getMinutes();
}

function maxConcurrent(sessions, startMin, endMin, tutorUid) {
  const events = [];

  for (const session of sessions || []) {
    if (tutorUid && session.tutorUid !== tutorUid) continue;
    if (isCanceledSt(session.status)) continue;

    const sessionStart = toMin(session.start);
    const sessionEnd = toMin(session.end);
    if (!overlaps(sessionStart, sessionEnd, startMin, endMin)) continue;

    events.push({ minute: Math.max(sessionStart, startMin), delta: 1 });
    events.push({ minute: Math.min(sessionEnd, endMin), delta: -1 });
  }

  events.sort((a, b) => a.minute - b.minute || a.delta - b.delta);

  let active = 0;
  let peak = 0;
  for (const event of events) {
    active += event.delta;
    if (active > peak) peak = active;
  }

  return peak;
}

function canFit(sessions, tutorUid, startMin, endMin) {
  return maxConcurrent(sessions, startMin, endMin, tutorUid) < 3;
}

function seatsLeft(sessions, tutorUid, startMin, endMin) {
  return Math.max(0, 3 - maxConcurrent(sessions, startMin, endMin, tutorUid));
}

function isInAvailability(blocks, start, end) {
  for (const block of blocks || []) {
    if (block.unavailable) continue;
    const blockStart = new Date(block.start);
    const blockEnd = new Date(block.end);
    if (blockStart <= start && blockEnd >= end) return true;
  }
  return false;
}

function resolveLane(sessions, startMin, endMin, tutorUid) {
  const entries = (sessions || [])
    .filter((session) => session.tutorUid === tutorUid && !isCanceledSt(session.status))
    .filter((session) => {
      const sessionStart = toMin(session.start);
      const sessionEnd = Math.max(sessionStart + 5, toMin(session.end));
      return overlaps(sessionStart, sessionEnd, startMin, endMin);
    })
    .map((session) => {
      const sessionStart = toMin(session.start);
      return {
        id: session.id,
        startMin: sessionStart,
        endMin: Math.max(sessionStart + 5, toMin(session.end)),
      };
    });

  entries.push({ id: "__candidate__", startMin, endMin });
  entries.sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    if (a.endMin !== b.endMin) return a.endMin - b.endMin;
    if (a.id === "__candidate__") return 1;
    if (b.id === "__candidate__") return -1;
    return 0;
  });

  const laneEnds = [-Infinity, -Infinity, -Infinity];
  const laneById = new Map();

  for (const entry of entries) {
    let lane = laneEnds.findIndex((value) => value <= entry.startMin);
    if (lane === -1) {
      if (entry.id === "__candidate__") return null;
      lane = laneEnds.indexOf(Math.min(...laneEnds));
    }

    laneEnds[lane] = Math.max(laneEnds[lane], entry.endMin);
    laneById.set(entry.id, lane);
  }

  return laneById.get("__candidate__") ?? null;
}

function captureAnchor(el) {
  if (!el) return null;
  const containerRect = el.getBoundingClientRect();
  const nodes = el.querySelectorAll("[data-month-key]");
  for (const node of nodes) {
    const rect = node.getBoundingClientRect();
    if (rect.bottom > containerRect.top) {
      return { key: node.getAttribute("data-month-key"), top: rect.top - containerRect.top };
    }
  }
  return null;
}

function buildDateTime(targetDate, minute) {
  return new Date(
    targetDate.getFullYear(),
    targetDate.getMonth(),
    targetDate.getDate(),
    Math.floor(minute / 60),
    minute % 60,
    0,
    0
  );
}

function normalizeTimeRange(startMin, endMin, bounds) {
  const rangeStart = Number(bounds?.startMin ?? 0);
  const rangeEnd = Number(bounds?.endMin ?? (24 * 60));
  const safeStart = Math.min(Math.max(startMin, rangeStart), Math.max(rangeStart, rangeEnd - 5));
  const safeEnd = Math.min(rangeEnd, Math.max(safeStart + 5, endMin));
  return { startMin: safeStart, endMin: safeEnd };
}

export default function ModalCalendarPanel({
  studentId,
  studentName,
  selectedDate,
  selectedSessionId,
  onDayClick,
  onSessionClick,
  tutors = [],
  draftSession = null,
  allowSessionTransfers = false,
  onMoveSessionDraft = null,
  onPasteSessionDraft = null,
}) {
  const BASE = useMemo(() => startOfMonth(new Date()), []);
  const today = useMemo(() => new Date(), []);

  const [months, setMonths] = useState(() =>
    Array.from({ length: PAST_INIT + 1 + FUTURE_INIT }, (_, index) => addMonths(BASE, index - PAST_INIT))
  );
  const [pastCount, setPastCount] = useState(PAST_INIT);

  const [sessions, setSessions] = useState([]);
  const [dayConfigsMap, setDayConfigsMap] = useState({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [draggingSession, setDraggingSession] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [copiedSession, setCopiedSession] = useState(null);

  const [conflictDialog, setConflictDialog] = useState(null);
  const [conflictStartMin, setConflictStartMin] = useState(0);
  const [conflictEndMin, setConflictEndMin] = useState(0);
  const [conflictTutor, setConflictTutor] = useState("");
  const [conflictTutorOptions, setConflictTutorOptions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const containerRef = useRef(null);
  const currentMonthRef = useRef(null);
  const isLoadingPastRef = useRef(false);
  const isLoadingFutRef = useRef(false);
  const loadedStartRef = useRef(-PAST_INIT);
  const loadedEndRef = useRef(FUTURE_INIT);
  const epochRef = useRef(0);
  const subscriptionRef = useRef(null);
  const topSentinelRef = useRef(null);
  const bottomSentinelRef = useRef(null);

  const setupSubscription = useCallback((sid) => {
    subscriptionRef.current?.();
    subscriptionRef.current = null;
    setSessions([]);
    if (!sid) return;

    const subStart = addMonths(BASE, -SUB_PAST_MONTHS);
    const subEnd = addMonths(BASE, SUB_FUTURE_MONTHS + 1);
    subscriptionRef.current = subscribeToStudentSessionsInRange(sid, subStart, subEnd, (rows) => setSessions(rows));
  }, [BASE]);

  useEffect(() => () => { subscriptionRef.current?.(); }, []);

  useEffect(() => {
    epochRef.current += 1;
    const epoch = epochRef.current;

    const initMonths = Array.from({ length: PAST_INIT + 1 + FUTURE_INIT }, (_, index) => addMonths(BASE, index - PAST_INIT));
    setMonths(initMonths);
    setPastCount(PAST_INIT);
    loadedStartRef.current = -PAST_INIT;
    loadedEndRef.current = FUTURE_INIT;
    setDayConfigsMap({});

    setTimeout(() => {
      currentMonthRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    }, 80);

    setupSubscription(studentId);

    (async () => {
      try {
        const rangeMonths = Array.from({ length: PAST_INIT + 1 + FUTURE_INIT }, (_, index) => addMonths(BASE, index - PAST_INIT));
        const allDays = rangeMonths.flatMap((month) => monthGridDays(month));
        const cfgs = await fetchDayConfigsForDates(allDays);
        if (epochRef.current !== epoch) return;
        setDayConfigsMap(cfgs);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [BASE, setupSubscription, studentId]);

  const loadMoreFuture = useCallback(async () => {
    if (isLoadingFutRef.current) return;
    isLoadingFutRef.current = true;
    setIsLoadingMore(true);

    const prevEnd = loadedEndRef.current;
    const newEnd = prevEnd + LOAD_FUTURE;
    const newMonths = Array.from({ length: LOAD_FUTURE }, (_, index) => addMonths(BASE, prevEnd + 1 + index));

    try {
      const cfgs = await fetchDayConfigsForDates(newMonths.flatMap((month) => monthGridDays(month)));
      setDayConfigsMap((prev) => ({ ...prev, ...cfgs }));
      setMonths((prev) => [...prev, ...newMonths]);
      loadedEndRef.current = newEnd;
    } catch (error) {
      console.error(error);
    } finally {
      isLoadingFutRef.current = false;
      setIsLoadingMore(false);
    }
  }, [BASE]);

  const loadMorePast = useCallback(async () => {
    if (isLoadingPastRef.current) return;
    isLoadingPastRef.current = true;

    const prevStart = loadedStartRef.current;
    const newStart = prevStart - LOAD_PAST;
    const newMonths = Array.from({ length: LOAD_PAST }, (_, index) => addMonths(BASE, newStart + index));

    try {
      const cfgs = await fetchDayConfigsForDates(newMonths.flatMap((month) => monthGridDays(month)));
      const el = containerRef.current;
      const anchor = el ? captureAnchor(el) : null;

      flushSync(() => {
        setDayConfigsMap((prev) => ({ ...prev, ...cfgs }));
        setMonths((prev) => [...newMonths, ...prev]);
        setPastCount((prev) => prev + LOAD_PAST);
        loadedStartRef.current = newStart;
      });

      if (el && anchor?.key) {
        requestAnimationFrame(() => {
          const containerRect = el.getBoundingClientRect();
          const anchorNode = el.querySelector(`[data-month-key="${anchor.key}"]`);
          if (!anchorNode) return;
          el.scrollTop += anchorNode.getBoundingClientRect().top - containerRect.top - anchor.top;
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      isLoadingPastRef.current = false;
    }
  }, [BASE]);

  useEffect(() => {
    const root = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (entry.target === topSentinelRef.current) loadMorePast();
          if (entry.target === bottomSentinelRef.current) loadMoreFuture();
        }
      },
      { root, rootMargin: "300px" }
    );

    if (topSentinelRef.current) observer.observe(topSentinelRef.current);
    if (bottomSentinelRef.current) observer.observe(bottomSentinelRef.current);
    return () => observer.disconnect();
  }, [loadMoreFuture, loadMorePast]);

  const displaySessions = useMemo(() => {
    const merged = [...sessions];

    if (draftSession && draftSession.studentId === studentId && draftSession.start) {
      const preview = {
        ...draftSession,
        id: draftSession.id || "__draft__",
        isDraftPreview: !draftSession.id,
      };
      const existingIndex = preview.id ? merged.findIndex((session) => session.id === preview.id) : -1;
      if (existingIndex >= 0) {
        merged.splice(existingIndex, 1, preview);
      } else {
        merged.push(preview);
      }
    }

    return merged;
  }, [draftSession, sessions, studentId]);

  const sessionsByDay = useMemo(() => {
    const map = new Map();

    for (const session of displaySessions) {
      const start = session.start instanceof Date ? session.start : (session.start?.toDate?.() ?? new Date(session.start));
      if (!start || Number.isNaN(start.getTime())) continue;
      const key = ymd(start);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(session);
    }

    for (const [key, daySessions] of map) {
      daySessions.sort((a, b) => {
        const aTime = a.start instanceof Date ? a.start : new Date(a.start);
        const bTime = b.start instanceof Date ? b.start : new Date(b.start);
        return aTime - bTime;
      });
      map.set(key, daySessions);
    }

    return map;
  }, [displaySessions]);

  const computeTutorOptions = useCallback((availability, scheduledSessions, targetDate, startMin, endMin) => {
    const start = buildDateTime(targetDate, startMin);
    const end = buildDateTime(targetDate, endMin);

    return (tutors || []).map((tutor) => {
      const tutorBlocks = availability.filter((block) => block.uid === tutor.id);
      if (!isInAvailability(tutorBlocks, start, end)) return null;
      if (!canFit(scheduledSessions, tutor.id, startMin, endMin)) return null;
      return {
        ...tutor,
        seatsLeft: seatsLeft(scheduledSessions, tutor.id, startMin, endMin),
      };
    }).filter(Boolean);
  }, [tutors]);

  const validateForDate = useCallback(async (session, targetDate, forPaste) => {
    const startOfDay = new Date(targetDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(targetDate);
    endOfDay.setHours(23, 59, 59, 999);

    try {
      const [availability, daySessions, configs] = await Promise.all([
        fetchAvailabilityBlocks({ rangeStart: startOfDay, rangeEnd: endOfDay }),
        fetchScheduledSessions({ rangeStart: startOfDay, rangeEnd: endOfDay }),
        fetchDayConfigsForDates([startOfDay]),
      ]);

      const dateKey = ymd(targetDate);
      const cfg = configs?.[dateKey];
      const resolvedDay = resolveScheduleForDate(targetDate, cfg);
      if (resolvedDay.isClosed) {
        return { ok: false, reason: "This day is marked as closed.", availableTutors: [], ctx: {} };
      }

      const sourceStart = toDate(session.start);
      const sourceEnd = toDate(session.end);
      const startMin = sourceStart.getHours() * 60 + sourceStart.getMinutes();
      const endMin = sourceEnd.getHours() * 60 + sourceEnd.getMinutes();
      const targetStart = buildDateTime(targetDate, startMin);
      const targetEnd = buildDateTime(targetDate, endMin);

      const excludeId = forPaste ? null : session.id;
      const otherSessions = daySessions.filter((entry) => entry.id !== excludeId);
      const tutorAvailability = availability.filter((block) => block.uid === session.tutorUid);
      const tutorOk = isInAvailability(tutorAvailability, targetStart, targetEnd);
      const tutorHasCapacity = canFit(otherSessions, session.tutorUid, startMin, endMin);

      if (tutorOk && tutorHasCapacity) {
        return {
          ok: true,
          avail: availability,
          otherSess: otherSessions,
          startMin,
          endMin,
          cfg,
          dayRange: { startMin: resolvedDay.startMin, endMin: resolvedDay.endMin },
        };
      }

      const reason = !tutorOk
        ? `${session.tutorName || "The assigned tutor"} is not available on this day at ${fmtTime(sourceStart)}-${fmtTime(sourceEnd)}.`
        : `${session.tutorName || "The assigned tutor"} is fully booked at that time (max 3 concurrent students).`;

      return {
        ok: false,
        reason,
        availableTutors: computeTutorOptions(availability, otherSessions, targetDate, startMin, endMin),
        avail: availability,
        otherSess: otherSessions,
        startMin,
        endMin,
        cfg,
        dayRange: { startMin: resolvedDay.startMin, endMin: resolvedDay.endMin },
      };
    } catch (error) {
      console.error("ModalCalendarPanel validation error:", error);
      return { ok: false, reason: "Could not validate availability. Please try again.", availableTutors: [], ctx: {} };
    }
  }, [computeTutorOptions]);

  const buildSessionDraft = useCallback((session, targetDate, tutorId, tutorName, vCtx) => {
    const { startMin, endMin, otherSess, cfg } = vCtx;
    const timeBlocks = cfg?.timeBlocks || [];
    const block = timeBlocks.find((entry) => startMin >= entry.startMin && startMin < entry.endMin) || timeBlocks[0] || null;
    const lane = resolveLane(otherSess, startMin, endMin, tutorId);

    return {
      ...session,
      tutorUid: tutorId,
      tutorName,
      start: buildDateTime(targetDate, startMin),
      end: buildDateTime(targetDate, endMin),
      dayKey: targetDate.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase(),
      blockId: block?.id || session.blockId || "",
      slotIdx: lane ?? 0,
    };
  }, []);

  const executeMove = useCallback(async (session, targetDate, tutorId, tutorName, vCtx) => {
    const nextDraft = buildSessionDraft(session, targetDate, tutorId, tutorName, vCtx);
    if (typeof onMoveSessionDraft === "function") {
      onMoveSessionDraft(nextDraft);
      return;
    }

    await updateScheduledSession(session.id, {
      start: nextDraft.start,
      end: nextDraft.end,
      tutorUid: nextDraft.tutorUid,
      tutorName: nextDraft.tutorName,
      dayKey: nextDraft.dayKey,
      blockId: nextDraft.blockId,
      slotIdx: nextDraft.slotIdx,
      status: nextDraft.status,
      subject: nextDraft.subject,
      note: nextDraft.note,
    });
  }, [buildSessionDraft, onMoveSessionDraft]);

  const executePaste = useCallback(async (session, targetDate, tutorId, tutorName, vCtx) => {
    const nextDraft = buildSessionDraft(
      {
        ...session,
        id: undefined,
      },
      targetDate,
      tutorId,
      tutorName,
      vCtx
    );

    delete nextDraft.id;
    delete nextDraft.isDraftPreview;

    if (typeof onPasteSessionDraft === "function") {
      onPasteSessionDraft(nextDraft);
      return;
    }

    await createScheduledSession(nextDraft);
  }, [buildSessionDraft, onPasteSessionDraft]);

  const openConflictDialog = useCallback((mode, session, targetDate, validation) => {
    setConflictDialog({
      mode,
      session,
      targetDate,
      reason: validation.reason,
      availableTutors: validation.availableTutors,
      vCtx: validation,
    });
    setConflictStartMin(validation.startMin);
    setConflictEndMin(validation.endMin);
    setConflictTutor("");
  }, []);

  const handleSessionDragStart = useCallback((session) => {
    if (!allowSessionTransfers || session?.isDraftPreview) return;
    setDraggingSession(session);
    setDragOverDate(null);
  }, [allowSessionTransfers]);

  const handleDayDragOver = useCallback((date) => {
    if (!allowSessionTransfers) return;
    setDragOverDate(ymd(date));
  }, [allowSessionTransfers]);

  const handleDayDrop = useCallback(async (date) => {
    if (!allowSessionTransfers) return;

    const session = draggingSession;
    setDragOverDate(null);
    setDraggingSession(null);
    if (!session) return;

    const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const sourceDate = toDate(session.start);
    const sourceDay = new Date(sourceDate.getFullYear(), sourceDate.getMonth(), sourceDate.getDate());
    if (targetDate.getTime() === sourceDay.getTime()) return;

    if (isPastDateValue(targetDate)) {
      alert("Cannot move sessions to past dates.");
      return;
    }

    setIsProcessing(true);
    try {
      const validation = await validateForDate(session, targetDate, false);
      if (validation.ok) {
        await executeMove(session, targetDate, session.tutorUid, session.tutorName, validation);
      } else {
        openConflictDialog("move", session, targetDate, validation);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [allowSessionTransfers, draggingSession, executeMove, openConflictDialog, validateForDate]);

  useEffect(() => {
    if (!allowSessionTransfers) return undefined;
    const handler = () => {
      setDraggingSession(null);
      setDragOverDate(null);
    };
    document.addEventListener("dragend", handler);
    return () => document.removeEventListener("dragend", handler);
  }, [allowSessionTransfers]);

  useEffect(() => {
    if (!allowSessionTransfers) return undefined;

    const handler = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "c" && selectedSessionId) {
        const session = displaySessions.find((entry) => entry.id === selectedSessionId);
        if (session && !session.isDraftPreview && !isCanceledSt(session.status) && !isPastDateValue(toDate(session.start))) {
          setCopiedSession(session);
        }
      }

      if (event.key === "Escape") {
        setCopiedSession(null);
        setConflictDialog(null);
        setConflictTutor("");
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [allowSessionTransfers, displaySessions, selectedSessionId]);

  const handlePasteOnDay = useCallback(async (date) => {
    const session = copiedSession;
    if (!allowSessionTransfers || !session) return;

    const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (isPastDateValue(targetDate)) {
      alert("Cannot paste sessions to past dates.");
      return;
    }

    setIsProcessing(true);
    try {
      const validation = await validateForDate(session, targetDate, true);
      if (validation.ok) {
        await executePaste(session, targetDate, session.tutorUid, session.tutorName, validation);
        setCopiedSession(null);
      } else {
        openConflictDialog("paste", session, targetDate, validation);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [allowSessionTransfers, copiedSession, executePaste, openConflictDialog, validateForDate]);

  const handleDayClick = useCallback((date, isClosed) => {
    if (isClosed) return;
    if (allowSessionTransfers && copiedSession) {
      handlePasteOnDay(date);
      return;
    }
    onDayClick?.(date);
  }, [allowSessionTransfers, copiedSession, handlePasteOnDay, onDayClick]);

  const handleSessionClick = useCallback((sessionId) => {
    if (copiedSession && copiedSession.id !== sessionId) setCopiedSession(null);
    const session = displaySessions.find((entry) => entry.id === sessionId);
    if (!session) return;
    if (session.isDraftPreview && !draftSession?.id) return;
    onSessionClick?.(sessionId, session);
  }, [copiedSession, displaySessions, draftSession, onSessionClick]);

  const conflictDayRange = useMemo(() => {
    if (conflictDialog?.vCtx?.dayRange) return conflictDialog.vCtx.dayRange;

    const mins = (conflictDialog?.vCtx?.avail || []).flatMap((block) => ([
      minutesFromDate(new Date(block.start)),
      minutesFromDate(new Date(block.end)),
    ]));

    if (mins.length) {
      return {
        startMin: Math.min(...mins),
        endMin: Math.max(...mins),
      };
    }

    return { startMin: 8 * 60, endMin: 22 * 60 };
  }, [conflictDialog]);

  const conflictTimeOptions = useMemo(() => {
    if (!conflictDialog) return [];
    const latestStart = Math.max(conflictDayRange.startMin, conflictDayRange.endMin - 5);
    const options = buildTimeOptions(conflictDayRange.startMin, latestStart, 5);
    if (!options.includes(conflictStartMin)) options.push(conflictStartMin);
    return options.sort((a, b) => a - b);
  }, [conflictDayRange, conflictDialog, conflictStartMin]);

  const conflictEndOptions = useMemo(() => {
    if (!conflictDialog) return [];
    const minimumEnd = Math.min(conflictDayRange.endMin, conflictStartMin + 5);
    const options = buildTimeOptions(minimumEnd, conflictDayRange.endMin, 5);
    if (!options.includes(conflictEndMin)) options.push(conflictEndMin);
    return options.sort((a, b) => a - b).filter((value) => value > conflictStartMin);
  }, [conflictDayRange, conflictDialog, conflictEndMin, conflictStartMin]);

  useEffect(() => {
    if (!conflictDialog) return;

    const normalized = normalizeTimeRange(conflictStartMin, conflictEndMin, conflictDayRange);
    if (normalized.startMin !== conflictStartMin) {
      setConflictStartMin(normalized.startMin);
      return;
    }
    if (normalized.endMin !== conflictEndMin) {
      setConflictEndMin(normalized.endMin);
    }
  }, [conflictDayRange, conflictDialog, conflictEndMin, conflictStartMin]);

  useEffect(() => {
    if (!conflictDialog) {
      setConflictTutorOptions([]);
      return;
    }

    const normalized = normalizeTimeRange(conflictStartMin, conflictEndMin, conflictDayRange);
    const nextOptions = computeTutorOptions(
      conflictDialog.vCtx?.avail || [],
      conflictDialog.vCtx?.otherSess || [],
      conflictDialog.targetDate,
      normalized.startMin,
      normalized.endMin
    );
    setConflictTutorOptions(nextOptions);
  }, [computeTutorOptions, conflictDayRange, conflictDialog, conflictEndMin, conflictStartMin]);

  useEffect(() => {
    if (!conflictDialog) return;
    if (conflictTutor && !conflictTutorOptions.find((tutor) => tutor.id === conflictTutor)) {
      setConflictTutor("");
    }
  }, [conflictDialog, conflictTutor, conflictTutorOptions]);

  const conflictCanConfirm = !!(
    conflictDialog &&
    conflictTutor &&
    conflictEndMin > conflictStartMin &&
    conflictTutorOptions.find((tutor) => tutor.id === conflictTutor)
  );

  const handleConflictStartChange = useCallback((nextStartMin) => {
    const normalized = normalizeTimeRange(nextStartMin, conflictEndMin, conflictDayRange);
    setConflictStartMin(normalized.startMin);
    setConflictEndMin(normalized.endMin);
  }, [conflictDayRange, conflictEndMin]);

  const handleConflictEndChange = useCallback((nextEndMin) => {
    const normalized = normalizeTimeRange(conflictStartMin, nextEndMin, conflictDayRange);
    setConflictStartMin(normalized.startMin);
    setConflictEndMin(normalized.endMin);
  }, [conflictDayRange, conflictStartMin]);

  const handleConflictCancel = useCallback(() => {
    setConflictDialog(null);
    setConflictStartMin(0);
    setConflictEndMin(0);
    setConflictTutor("");
  }, []);

  const handleConflictConfirm = useCallback(async () => {
    if (!conflictDialog || !conflictCanConfirm) return;

    const tutor = conflictTutorOptions.find((entry) => entry.id === conflictTutor);
    if (!tutor) return;

    setIsProcessing(true);
    try {
      const resolvedCtx = {
        ...conflictDialog.vCtx,
        startMin: conflictStartMin,
        endMin: conflictEndMin,
      };

      if (conflictDialog.mode === "move") {
        await executeMove(conflictDialog.session, conflictDialog.targetDate, tutor.id, tutor.name, resolvedCtx);
      } else {
        await executePaste(conflictDialog.session, conflictDialog.targetDate, tutor.id, tutor.name, resolvedCtx);
        setCopiedSession(null);
      }

      setConflictDialog(null);
      setConflictStartMin(0);
      setConflictEndMin(0);
      setConflictTutor("");
    } finally {
      setIsProcessing(false);
    }
  }, [
    conflictCanConfirm,
    conflictDialog,
    conflictEndMin,
    conflictStartMin,
    conflictTutor,
    conflictTutorOptions,
    executeMove,
    executePaste,
  ]);

  if (!studentId) {
    return (
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#9ca3af",
        fontSize: 14,
        fontWeight: 500,
        padding: 32,
        textAlign: "center",
      }}>
        Select a student to see their calendar
      </div>
    );
  }

  const cellProps = {
    today,
    sessionsByDay,
    selectedDate,
    selectedSessionId,
    dayConfigsMap,
    isDeveloperMode: false,
    onDayClick: handleDayClick,
    onSessionClick: handleSessionClick,
    onNoteEnter: null,
    onNoteLeave: null,
    compact: true,
    onSessionDragStart: allowSessionTransfers ? handleSessionDragStart : null,
    draggingSessionId: allowSessionTransfers ? (draggingSession?.id || null) : null,
    onDayDragOver: allowSessionTransfers ? handleDayDragOver : null,
    onDayDrop: allowSessionTransfers ? handleDayDrop : null,
    dragOverDate: allowSessionTransfers ? dragOverDate : null,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", position: "relative" }}>
      {allowSessionTransfers && copiedSession && (
        <div style={{
          padding: "6px 12px",
          background: "#eff6ff",
          borderBottom: "1px solid #bae6fd",
          fontSize: 11,
          color: "#0369a1",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          gap: 8,
        }}>
          <span>
            Session copied - click an open day to paste, or{" "}
            <kbd style={{ background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 3, padding: "0 4px", fontSize: 10 }}>Esc</kbd>{" "}
            to cancel
          </span>
          <button
            onClick={() => setCopiedSession(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#0369a1", fontSize: 14, lineHeight: 1, padding: "0 2px" }}
          >
            x
          </button>
        </div>
      )}

      <div style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "#fff",
        borderBottom: "1px solid #e5e7eb",
        flexShrink: 0,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", padding: "0 8px" }}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => (
            <div key={day} style={{
              padding: "8px 2px",
              textAlign: "center",
              fontSize: 10,
              fontWeight: 600,
              color: index === 0 || index === 6 ? "#9ca3af" : "#6b7280",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
            }}>
              {day}
            </div>
          ))}
        </div>
      </div>

      <div
        ref={containerRef}
        style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: "#fff" }}
      >
        <div ref={topSentinelRef} style={{ height: 1 }} />

        <div style={{ padding: "0 8px 40px", minWidth: 0 }}>
          {months.map((month, index) => (
            <MonthSection
              key={monthKey(month)}
              month={month}
              monthRef={index === pastCount ? currentMonthRef : null}
              isCurrentMonth={index === pastCount}
              {...cellProps}
            />
          ))}
          {isLoadingMore && (
            <div style={{ textAlign: "center", padding: "16px 0", fontSize: 12, color: "#9ca3af" }}>
              Loading...
            </div>
          )}
        </div>

        <div ref={bottomSentinelRef} style={{ height: 1 }} />
      </div>

      {isProcessing && (
        <div style={{
          position: "absolute",
          inset: 0,
          background: "rgba(255,255,255,0.75)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 20,
          fontSize: 13,
          color: "#64748b",
          fontWeight: 500,
        }}>
          Validating...
        </div>
      )}

      {conflictDialog && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
          }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) handleConflictCancel();
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: "min(440px, calc(100vw - 32px))",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
              {conflictDialog.mode === "move" ? "Cannot Move Session" : "Cannot Paste Session"}
            </div>
            <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, marginBottom: 16 }}>
              {conflictDialog.reason}
            </div>

            <div className="sessModal__fields" style={{ gap: 10, marginBottom: 16 }}>
              <div className="sessModal__field">
                <label className="sessModal__label">Time</label>
                <div className="sessModal__timeRow">
                  <select
                    className="sessModal__select"
                    value={conflictStartMin}
                    onChange={(event) => handleConflictStartChange(Number(event.target.value))}
                  >
                    {conflictTimeOptions.map((value) => (
                      <option key={value} value={value}>{formatTimeFromMinutes(value)}</option>
                    ))}
                  </select>
                  <select
                    className="sessModal__select"
                    value={conflictEndMin}
                    onChange={(event) => handleConflictEndChange(Number(event.target.value))}
                  >
                    {conflictEndOptions.map((value) => (
                      <option key={value} value={value}>{formatTimeFromMinutes(value)}</option>
                    ))}
                  </select>
                </div>
                <div className="sessModal__infoRow">
                  Available: {formatTimeFromMinutes(conflictDayRange.startMin)} - {formatTimeFromMinutes(conflictDayRange.endMin)}
                </div>
              </div>

              <div className="sessModal__field">
                <label className="sessModal__label">Tutor</label>
                <select
                  className="sessModal__select"
                  value={conflictTutor}
                  onChange={(event) => setConflictTutor(event.target.value)}
                >
                  <option value="">Select tutor...</option>
                  {conflictTutorOptions.map((tutor) => (
                    <option key={tutor.id} value={tutor.id}>
                      {tutor.name || tutor.email} ({tutor.seatsLeft} left)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {conflictTutorOptions.length === 0 && (
              <div style={{
                fontSize: 12,
                color: "#b91c1c",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 16,
              }}>
                No tutors are available for this time. Adjust the time or cancel.
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={handleConflictCancel}
                style={{
                  padding: "0 16px",
                  height: 34,
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                  background: "#fff",
                  color: "#374151",
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                disabled={!conflictCanConfirm || isProcessing}
                onClick={handleConflictConfirm}
                style={{
                  padding: "0 16px",
                  height: 34,
                  border: "none",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: conflictCanConfirm ? "pointer" : "not-allowed",
                  background: conflictCanConfirm ? "#18181b" : "#d1d5db",
                  color: "#fff",
                  opacity: isProcessing ? 0.6 : 1,
                }}
              >
                {conflictDialog.mode === "move" ? "Update Session" : "Use This Draft"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
